import { useState } from 'react';
import { Button, IconButton, Stack, TextField, Tooltip, Snackbar, Alert } from '@mui/material';
import { Add, Edit, Delete, UploadFile } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ExportMenu } from '../../components/ExportMenu';
import { BulkImportDialog } from '../../components/BulkImportDialog';
import { EmployeeFormDialog } from './EmployeeFormDialog';
import { employeesApi, type Employee, type CreateEmployeeInput } from '../../api/employees.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

export function EmployeeListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Employee | null>(null);
  const [deleting, setDeleting] = useState<Employee | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [bulkOpen, setBulkOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['employees', page, limit, search],
    queryFn: () => employeesApi.list({ page, limit, search: search || undefined }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['employees'] });

  const createMut = useMutation({
    mutationFn: (input: CreateEmployeeInput) => employeesApi.create(input),
    onSuccess: () => { setFormOpen(false); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: CreateEmployeeInput }) => employeesApi.update(id, input),
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => employeesApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<Employee>[] = [
    { key: 'employeeCode', label: 'Employee ID', width: 120 },
    { key: 'name', label: 'Name' },
    { key: 'department', label: 'Department', render: (r) => r.department ?? '—' },
    { key: 'designation', label: 'Designation', render: (r) => r.designation ?? '—' },
    { key: 'reportingManagerName', label: 'Reporting Manager', render: (r) => r.reportingManagerName ?? '—' },
  ];

  return (
    <>
      <PageHeader
        title="Employee Master"
        action={
          <Stack direction="row" spacing={1}>
            <ExportMenu dataset="employees" permission="employee.export" filters={{ search: search || undefined }} />
            {hasPermission('employee.create') && <Button startIcon={<UploadFile />} variant="outlined" onClick={() => setBulkOpen(true)}>Bulk Upload</Button>}
            {hasPermission('employee.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setFormError(null); setFormOpen(true); }}>New Employee</Button>}
          </Stack>
        }
      />
      <Stack direction="row" mb={2}>
        <TextField size="small" placeholder="Search name / code / dept" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
      </Stack>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {hasPermission('employee.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setFormError(null); setFormOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('employee.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />
      <EmployeeFormDialog
        open={formOpen}
        initial={editing}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => {
          setFormError(null);
          if (editing) await updateMut.mutateAsync({ id: editing.id, input });
          else await createMut.mutateAsync(input);
        }}
      />
      <BulkImportDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        title="Bulk upload employees"
        entityLabel="employee"
        showCreateLogins={false}
        columns={[
          { header: 'Employee ID', sample: 'EMP-001', required: true },
          { header: 'Name', sample: 'Ramesh Kumar', required: true },
          { header: 'Department', sample: 'EPC' },
          { header: 'Designation', sample: 'Site Engineer' },
          { header: 'Reporting Manager', sample: 'Arvind Kumar' },
          { header: 'Email', sample: 'ramesh@company.com' },
          { header: 'Mobile', sample: '9876543210' },
        ]}
        onImport={(rows) => employeesApi.bulkImport(rows)}
        onDone={invalidate}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete employee"
        message={`Soft-delete ${deleting?.name}? Blocked if they manage others.`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
